import { toastController } from '@/controllers/toastController'
import { uiController } from '@/controllers/uiController'
import { api } from '@/helpers/api'
import { commonUtils } from '@/helpers/commonUtils'
import { userService } from '@/services/userService'
import { Worker } from '@react-pdf-viewer/core'
import classNames from 'classnames'
import { getDocument } from 'pdfjs-dist'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import FullscreenDrag, { FullscreenDragRef } from './FullscreenDrag/FullscreenDrag'
import { Guide } from './Guide/Guide'
import { PdfMarkdown } from './PdfMarkdown/PdfMarkdown'
import style from './PdfPro.module.less'
import { ProHeader } from './ProHeader/ProHeader'

// import { testData } from './PdfMarkdown/testData'
import { routerService } from '@/services/routerService'
import { Alert, Button, Input, Modal, Progress, Spin, Tooltip } from 'antd'
import { MiddleStatus } from './MiddleStatus/MiddleStatus'
import { ProPdfTranslateHelper, RenderType } from './TranslateUtil/ProPdfTranslateHelper'
import { UploadHistoryModal } from './UploadHistoryModal'

import { LanguagePopover } from '@/components/LanguagePopover/LanguagePopover'
import { ModelPopover } from '@/components/ModelPopover/ModelPopover'
import { SearchPopover } from '@/components/SearchPopover /SearchPopover'
import { adService } from '@/services/adService'
import { translateService, TranslateServiceType } from '@/services/translateService'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { isNil, throttle } from 'lodash'
import { observer } from 'mobx-react'
import { useReactToPrint } from 'react-to-print'
import { CompareTranslateDialog } from './CompareTranslateDialog/CompareTranslateDialog'

import { LoadingDot } from '@/components/LoadingDot/LoadingDot'
import { envHelper } from '@/helpers/envHelper'
import { MODEL_STRATEGY_CONFIG } from '@/helpers/modelConsts'
import { TranslateServiceConsts } from '@/services/translateService/consts'
import { detect } from 'tinyld'

export const PDF_TYPE = 'application/pdf'
export const MAX_PDF_TRANSLATE_PAGE = 500

type ImageData = {
  id: string
  top_left_x: number
  top_left_y: number
  bottom_right_x: number
  bottom_right_y: number
  image_base64: string
}

type PageData = {
  index: number
  markdown: string
  images: ImageData[]
  dimensions: {
    dpi: number
    width: number
    height: number
  }
}

export const proPdfTranslateHelper = new ProPdfTranslateHelper(userService.pdfProTranslateModel)

const PAGE_SIZE_THRESHOLD = 50

// 处理浮点数精度问题，保留1位小数
const roundToDecimal = (value: number, decimals: number = 1): number => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)
}

export const PdfPro = observer(() => {
  const [uploadingPdf, setUploadingPdf] = useState(false)
  const [paringPdf2markdown, setParingPdf2markdown] = useState(false)
  const [uploadPdfPercent, setUploadPdfPercent] = useState(0)
  const [parsePdfPercent, setParsePdfPercent] = useState(0)
  const [parsePdfError, setParsePdfError] = useState(false)
  const [curState, setCurState] = useState<'normal' | 'success' | 'middle'>('normal')
  const [markdownContent, setMarkdownContent] = useState('')
  const [pdfFileId, setPdfFileId] = useState<number | null>(null)
  const [markdownFileId, setMarkdownFileId] = useState<number | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState('')

  // 图片翻译相关状态
  const [imageTranslateMode, setImageTranslateMode] = useState<'manual' | 'auto'>('manual')

  const translatedImages = useRef<Map<string, string>>(new Map())
  const translatingImages = useRef<Set<string>>(new Set())

  const [showImageQuotaAlert, setShowImageQuotaAlert] = useState(false) // 自动翻译额度不足提示条

  // 更多配置面板显示状态
  const [showMoreConfigModal, setShowMoreConfigModal] = useState(false)
  const [serverDownloadInfo, setServerDownloadInfo] = useState<{
    showDownloadModal: boolean
    time: number
    isAIModel: boolean
    isFreeModel: boolean
  }>({ showDownloadModal: false, time: 0, isAIModel: false, isFreeModel: false })

  const curPdf2MarkdownFileId = useRef<number | null>(null)
  const checkPdf2MarkdownStatusTimer = useRef<NodeJS.Timeout | undefined>(undefined)

  const xhr = useRef<XMLHttpRequest | null>(null)
  const uploadSignUrlController = useRef<AbortController | null>(null)
  const updateUploadController = useRef<AbortController | null>(null)
  const pdf2markdownController = useRef<AbortController | null>(null)
  const checkPdf2MarkdownController = useRef<AbortController | null>(null)

  const [dragAreaRef, setDragAreaRef] = useState<HTMLDivElement | null>(null)
  const [pageData, setPageData] = useState<PageData[]>([])

  const [pdfTotalPage, setPdfTotalPage] = useState<number>(0)
  const [downloadPercent, setDownloadPercent] = useState(0)
  const [startDownload, setStartDownload] = useState(false)
  const [styleDefaultValue, setStyleDefaultValue] =
    useState<TranslateServiceType.StyleType>('dualGrey')
  const [serverDownloading, setServerDownloading] = useState(false)
  const [glossaryMode, setGlossaryMode] = useState<TranslateServiceType.GlossaryMode>(
    userService.pdfProGlossaryMode
  )

  // 原文和译文大小调整（全局配置，直接使用 userService 的值）
  // 由于组件是 observer，userService 的变化会自动触发重新渲染
  const originalTextScale = roundToDecimal(userService.pdfProOriginalTextScale, 1)
  const translationTextScale = roundToDecimal(userService.pdfProTranslationTextScale, 1)

  // 输入框的显示值（允许为空字符串）
  const [originalTextScaleInput, setOriginalTextScaleInput] = useState<string>(
    String(originalTextScale)
  )
  const [translationTextScaleInput, setTranslationTextScaleInput] = useState<string>(
    String(translationTextScale)
  )

  // 添加分页相关的状态
  const [totalPages, setTotalPages] = useState(0)
  const [visiblePages, setVisiblePages] = useState<number[]>([])
  const [pageSize, setPageSize] = useState(20) // 每次显示20页
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const isServerDownloadAction = useRef(false)
  const isLocalDownloadAction = useRef(false)
  const pendingPageLoadResolve = useRef<((value: void) => void) | null>(null)
  const expectedPageCount = useRef<number>(0)
  const loadedPageCount = useRef<number>(0)

  const fullscreenDragRef = useRef<FullscreenDragRef>(null)
  const [showHistory, setShowHistory] = useState(false)
  // 在需要的时候调用
  const setShowBlurArea = (value: boolean) => {
    fullscreenDragRef.current?.setShowBlurArea(value)
  }

  const uploadPercent = useMemo(() => {
    const percent = uploadPdfPercent + parsePdfPercent

    const totalPercent = percent / 2
    return totalPercent
  }, [uploadPdfPercent, parsePdfPercent])

  async function modelChanged(model: ServerDataTypes.Model) {
    if (userService.checkTokenCountByModel(model) <= 0) {
      toastController.warning('模型token已不足')
      userService.doPay({
        from: 'pdf_pro',
        feature: 'pdfPro',
      })
      return
    }

    // 保存用户手动选择的模型偏好
    userService.savePdfProUserSelectedModel(model)
    proPdfTranslateHelper.setModel(model)

    await new Promise((resolve) => setTimeout(resolve, 100))
    // 保存模型设置
    if (pdfFileId) {
      saveFileSettings(pdfFileId, {
        model: model,
        sourceLang: userService.pdfProSourceLang,
        targetLang: userService.pdfProTargetLang,
        styleType: styleDefaultValue,
        imageTranslateMode: imageTranslateMode,
      })
    }

    restartTranslate(styleDefaultValue)
  }

  async function languageChanged(type: 'source' | 'target', language: string) {
    if (userService.checkTokenCountByModel(userService.pdfProTranslateModel) <= 0) {
      toastController.warning('模型token已不足')
      userService.doPay({
        from: 'pdf_pro',
        feature: 'pdfPro',
      })
      return
    }
    if (type === 'source') {
      userService.pdfProSourceLang = language
    } else {
      userService.pdfProTargetLang = language
      resetImageTranslation()
    }

    // 如果有fileId，保存当前设置
    if (pdfFileId) {
      saveFileSettings(pdfFileId, {
        model: proPdfTranslateHelper.model,
        sourceLang: userService.pdfProSourceLang,
        targetLang: userService.pdfProTargetLang,
        styleType: styleDefaultValue,
        imageTranslateMode: imageTranslateMode,
      })
    }

    await new Promise((resolve) => setTimeout(resolve, 100))
    restartTranslate(styleDefaultValue)
  }

  const onPageLoad = useCallback(
    async (container: HTMLElement) => {
      loadedPageCount.current += 1

      if (isServerDownloadAction.current) {
        return
      }

      if (isLocalDownloadAction.current) {
        // 检查是否所有页面都已加载完成
        if (
          loadedPageCount.current >= expectedPageCount.current &&
          pendingPageLoadResolve.current
        ) {
          pendingPageLoadResolve.current()
          pendingPageLoadResolve.current = null
        }
        return
      }

      proPdfTranslateHelper.styleType = styleDefaultValue
      if (styleDefaultValue === 'onlyTranslation') {
        await proPdfTranslateHelper.doTranslate('pdf', [container], 'translation')
      } else {
        await proPdfTranslateHelper.doTranslate('pdf', [container], 'parallel')
      }
    },
    [styleDefaultValue]
  )

  const onImageLoad = useCallback(
    (imageId: string) => {
      const isTranslated = translatedImages.current.has(imageId)
      const isTranslating = translatingImages.current.has(imageId)
      const translatedSrc = translatedImages.current.get(imageId) ?? ''

      return {
        isTranslated,
        isTranslating,
        translatedSrc,
      }
    },
    [translatedImages, translatingImages]
  )

  // 样式切换时的逻辑（不重置图片状态）
  function styleChanged(styleValue: TranslateServiceType.StyleType) {
    // const container = document.querySelector('#pdf-markdown-content') as HTMLElement

    proPdfTranslateHelper.changeStyleType(styleValue)

    setStyleDefaultValue(styleValue)

    // 如果有fileId，保存当前设置
    if (pdfFileId) {
      saveFileSettings(pdfFileId, {
        model: proPdfTranslateHelper.model,
        sourceLang: userService.pdfProSourceLang,
        targetLang: userService.pdfProTargetLang,
        styleType: styleValue,
        imageTranslateMode: imageTranslateMode,
      })
    }
    // 关闭更多配置面板
    setShowMoreConfigModal(false)
  }

  function saveFileSettings(
    fileId: number,
    params: {
      model: ServerDataTypes.Model
      sourceLang: string
      targetLang: string
      styleType: TranslateServiceType.StyleType
      imageTranslateMode: 'manual' | 'auto'
      contextType?: PostDataTypes.ContextType
      glossaryMode?: TranslateServiceType.GlossaryMode
    }
  ) {
    if (fileId) {
      userService.savePdfProFileSettings(fileId, {
        ...params,
      })
    }
  }

  // 图片翻译功能函数
  const translateImage = useCallback(
    async (imageId: string, imageSrc: string, isAutoTranslate = false) => {
      console.log('translateImage 被调用：', {
        imageId,
        imageSrc: imageSrc.substring(0, 50) + '...',
        isAutoTranslate,
      })

      if (!userService.isLogin) {
        toastController.warning('请登录后使用该功能')
        return
      }

      if (userService.remainImageTranslateCount <= 0) {
        if (isAutoTranslate) {
          // 自动翻译模式下显示顶部提示条
          setShowImageQuotaAlert(true)
          // 自动翻译额度不足时，关闭自动翻译模式
          setImageTranslateMode('manual')
        } else {
          // 手动翻译模式下显示 Toast（固定定位）
          toastController.warning('图片翻译额度不够，请升级会员获取更多额度')
        }
        return
      }

      console.log('开始翻译图片，设置 translatingImages 状态')
      translatingImages.current.add(imageId)
      try {
        const fullResultDataUrl = await fetchTranslateImage(imageSrc)
        translatedImages.current.set(imageId, fullResultDataUrl)
        return fullResultDataUrl
      } catch (error) {
        console.error('translateImage catch error:', error)
        toastController.error(
          commonUtils.formatErrorMsg(error, '图片翻译失败，请稍后重试或联系客服')
        )
      } finally {
        console.log('翻译完成，清除 translatingImages 状态')
        translatingImages.current.delete(imageId)
      }
    },
    []
  )

  const fetchTranslateImage = useCallback((imageSrc: string) => {
    // 提取元数据，用于后续拼接完整的 data URL
    const metaDataAndBase64 = imageSrc.split(',')
    const metaData = metaDataAndBase64[0] // 如 'data:image/jpeg;base64'

    return new Promise<string>((resolve, reject) => {
      translateService.translateImage(
        {
          base64: imageSrc,
          fromLang: userService.pdfProSourceLang,
          toLang: userService.pdfProTargetLang,
          module: 'translate_pic',
        },
        (err: string | null, resultDataUrl: string) => {
          if (err) {
            reject(new Error(err))
          } else {
            const fullResultDataUrl = `${metaData},${resultDataUrl}`
            resolve(fullResultDataUrl)
          }
        }
      )
    })
  }, [])

  // 翻译图片函数
  const translateImageToTranslated = useCallback(
    async (imageId: string, imageSrc: string): Promise<boolean> => {
      console.log('开始翻译图片:', imageId)

      const loadingIcon = document.querySelector(
        `[data-image-id="${imageId}"] .loadingIcon`
      ) as HTMLElement
      const translateIcon = document.querySelector(
        `[data-image-id="${imageId}"] .translateIcon`
      ) as HTMLElement
      const imageIcon = document.querySelector(
        `[data-image-id="${imageId}"] .imageIcon`
      ) as HTMLImageElement

      // 显示加载状态
      loadingIcon.style.display = 'block'
      translateIcon.style.display = 'none'

      try {
        // 执行翻译
        const fullResultDataUrl = await translateImage(imageId, imageSrc, false)

        if (fullResultDataUrl) {
          // 翻译成功后更新状态和DOM
          translatedImages.current.set(imageId, fullResultDataUrl)

          const imgEl = document.getElementById(imageId) as HTMLImageElement | null
          if (imgEl) {
            if (!imgEl.dataset.originalSrc) {
              imgEl.dataset.originalSrc = imageSrc
            }
            imgEl.src = fullResultDataUrl
            imgEl.dataset.translatedSrc = fullResultDataUrl
            imgEl.dataset.isTranslated = '1'
          }

          loadingIcon.style.display = 'none'
          translateIcon.style.display = 'flex'
          translateIcon.title = '切换为原图'
          imageIcon.src = imageIcon.dataset.origin as string

          userService.updateUserInfo()
          return true
        } else {
          // 翻译失败
          loadingIcon.style.display = 'none'
          translateIcon.style.display = 'flex'
          return false
        }
      } catch (error) {
        console.error('翻译图片失败:', error)
        loadingIcon.style.display = 'none'
        translateIcon.style.display = 'flex'
        return false
      }
    },
    [translateImage]
  )

  // 还原图片函数
  const restoreImageToOriginal = useCallback(
    (imageId: string): boolean => {
      console.log('切换回原图:', imageId)

      try {
        const loadingIcon = document.querySelector(
          `[data-image-id="${imageId}"] .loadingIcon`
        ) as HTMLElement
        const translateIcon = document.querySelector(
          `[data-image-id="${imageId}"] .translateIcon`
        ) as HTMLElement
        const imageIcon = document.querySelector(
          `[data-image-id="${imageId}"] .imageIcon`
        ) as HTMLImageElement

        // 从翻译状态中移除
        translatedImages.current.delete(imageId)

        // 恢复原图
        const imgEl = document.getElementById(imageId) as HTMLImageElement | null
        if (imgEl) {
          const original = imgEl.dataset.originalSrc
          if (original) {
            imgEl.src = original
          } else {
            // 兜底：从内存 pageData 查原图
            const found = pageData.flatMap((p) => p.images).find((x) => x.id === imageId)
            if (found?.image_base64) imgEl.src = found.image_base64
          }
          imgEl.dataset.isTranslated = '0'
        }

        // 更新UI状态
        loadingIcon.style.display = 'none'
        translateIcon.style.display = 'flex'
        translateIcon.title = '翻译图片'
        imageIcon.src = imageIcon.dataset.target as string

        return true
      } catch (error) {
        console.error('还原图片失败:', error)
        return false
      }
    },
    [pageData]
  )

  const toggleImageTranslation = useCallback(
    async (imageId: string, imageSrc: string) => {
      console.log('toggleImageTranslation 被调用：', {
        imageId,
        imageSrc: imageSrc.substring(0, 50) + '...',
        translatedImages: translatedImages.current.size,
        translatingImages: translatingImages.current.size,
      })

      // 正在翻译中，返回
      if (translatingImages.current.has(imageId)) {
        return
      }

      const isTranslated = translatedImages.current.has(imageId)
      console.log('图片翻译状态：', { isTranslated })

      if (isTranslated) {
        // 如果已翻译，切换回原图
        restoreImageToOriginal(imageId)
      } else {
        // 如果未翻译，进行翻译
        await translateImageToTranslated(imageId, imageSrc)
      }
    },
    [translateImageToTranslated, restoreImageToOriginal]
  )

  // 重置图片翻译状态的函数
  const resetImageTranslation = () => {
    translatedImages.current = new Map()
    translatingImages.current = new Set()
    setShowImageQuotaAlert(false)

    restoreAllImagesToOriginal()
  }

  const restoreAllImagesToOriginal = useCallback(() => {
    const container = document.querySelector('#pdf-markdown-content') as HTMLElement
    if (container) {
      const images = container.querySelectorAll('img[id]')
      images.forEach((img) => {
        const imageId = img.id
        restoreImageToOriginal(imageId)
      })
    }
  }, [restoreImageToOriginal])

  // 自动翻译检查函数
  const checkAutoTranslate = useCallback(() => {
    if (imageTranslateMode !== 'auto') return

    const container = document.querySelector('#pdf-markdown-content')
    if (!container) return

    const images = container.querySelectorAll('img[id]')
    images.forEach((img) => {
      const imageId = img.id
      const rect = img.getBoundingClientRect()
      const windowHeight = window.innerHeight

      // 检查图片2/3部分是否在视窗中
      const visibleTop = Math.max(0, -rect.top)
      const visibleHeight = Math.min(rect.height, windowHeight - Math.max(0, rect.top))
      const visibleRatio = visibleHeight / rect.height

      if (
        visibleRatio >= 2 / 3 &&
        !translatedImages.current.has(imageId) &&
        !translatingImages.current.has(imageId)
      ) {
        const imageSrc = (img as HTMLImageElement).src
        translateImageToTranslated(imageId, imageSrc)
      }
    })
  }, [imageTranslateMode, translateImageToTranslated])

  function handleChooseFile() {
    commonUtils.selectFile(
      (fileList) => {
        if (fileList.length) {
          const file = fileList[0]
          afterChooseFile(file)
        }
      },
      {
        accept: [PDF_TYPE],
      }
    )
  }

  const handleNewUser = () => {
    adService.addBingUetqToHead('signup', {})
  }

  function afterChooseFile(file: File, fromDrag?: boolean) {
    if (!userService.isLogin) {
      toastController.warning('请登录后使用该功能')
      setShowBlurArea(false)

      uiController.loginModalProps = {
        open: true,
        handleNewUser,
      }

      return
    }

    uploadFile(file, fromDrag)
  }

  async function uploadFile(file: File, fromDrag?: boolean) {
    if (uploadingPdf) {
      toastController.warning(`文件上传中，请稍后重试`)
      setShowBlurArea(false)
      return
    }
    if (paringPdf2markdown) {
      toastController.warning(`您当前有文件正在转换中，请稍后重试`)
      setShowBlurArea(false)
      return
    }

    const loadingTask = await getDocument(URL.createObjectURL(file)).promise
    const numPages = loadingTask.numPages

    console.log('总页数：', numPages)

    setFile(file)
    setPdfTotalPage(numPages)
    setShowBlurArea(false)
    setCurState('middle')
    // await startUploadFile(file, numPages)
  }

  async function startUploadFile(params: {
    file: File
    totalPages: number
    start: number
    end: number
    targetLang: string
    model: ServerDataTypes.Model
    contextType: PostDataTypes.ContextType
    glossaryMode: TranslateServiceType.GlossaryMode
    fromDrag?: boolean
  }) {
    const { file, totalPages, start, end, targetLang, model, fromDrag, contextType, glossaryMode } =
      params

    let pageCount = totalPages
    if (start === end) {
      pageCount = 1
    } else {
      pageCount = end - start + 1
    }

    if (pageCount > userService.remainPdfProPageCount) {
      setShowBlurArea(false)
      toastController.warning(
        `您当前的翻译页数不足,剩余${userService.remainPdfProPageCount}页,上传的页数是:${pageCount}页`
      )
      return
    }

    // 设置翻译模型
    proPdfTranslateHelper.setModel(model)

    setFileName(file.name)

    // setCurState('normal')
    setShowBlurArea(true)
    setUploadingPdf(true)
    setUploadPdfPercent(0)
    try {
      uploadSignUrlController.current = new AbortController()
      const {
        // display为true代表 翻译了全部页数
        data: { fileId, fileName, signUrl, hasUpload, display },
      } = await api.pdf.getPdfProUploadSignUrl(
        {
          contentLength: file.size,
          filename: file.name,
          module: 'pdf_pro',
          pageNum: totalPages,
        },
        uploadSignUrlController.current
      )
      setPdfFileId(fileId)
      console.log('@@@fileId:', fileId)
      xhr.current = new XMLHttpRequest()
      xhr.current.open('PUT', signUrl, true)
      xhr.current.upload.onprogress = (e) => {
        const percentNumber = Math.round((e.loaded / e.total) * 10000) / 100
        setUploadPdfPercent(percentNumber)
      }
      xhr.current.onload = async () => {
        try {
          updateUploadController.current = new AbortController()
          await api.pdf.updateUploadStatus(
            { fileId, decry: false, source: 'pdf_pro' },
            updateUploadController.current
          )
        } catch (error) {
          toastController.error(commonUtils.formatErrorMsg(error))
          setShowBlurArea(false)
          setUploadingPdf(false)
          setUploadPdfPercent(0)
          setParingPdf2markdown(false)
          return
        }
        setUploadingPdf(false)
        setUploadPdfPercent(100)

        createPdf2markdownTask({
          fileId,
          fileName,
          decryCount: pageCount,
          display: false,
          numPages: totalPages,
          start,
          end,
          contextType,
          glossaryMode,
        })
        userService.updateUserInfo()
      }
      xhr.current.onerror = (error) => {
        toastController.error(
          commonUtils.formatErrorMsg(error, '文件上传失败，请稍后重试或联系客服')
        )
        setUploadingPdf(false)
        setShowBlurArea(false)
        setParingPdf2markdown(false)
      }
      xhr.current.onabort = () => {
        toastController.warning('取消文件上传')
        setUploadingPdf(false)
        setUploadPdfPercent(0)
        setShowBlurArea(false)
        setParingPdf2markdown(false)
      }
      xhr.current.send(file)
    } catch (error) {
      if (error.name === 'AbortError') {
        toastController.warning('取消文件上传')
      } else {
        toastController.error(
          commonUtils.formatErrorMsg(error, '文件上传意外失败，请稍后重试或联系客服')
        )
      }
      setUploadingPdf(false)
      setUploadPdfPercent(0)
      setShowBlurArea(false)
      setParingPdf2markdown(false)
    }
  }

  // 加载更多页面的函数
  const loadMorePages = useCallback(() => {
    if (isLoadingMore || visiblePages.length >= totalPages) return

    setIsLoadingMore(true)

    // 模拟异步加载
    setTimeout(() => {
      const nextStartIndex = visiblePages.length
      const nextEndIndex = Math.min(nextStartIndex + pageSize, totalPages)

      const newVisiblePages = pageData.slice(nextStartIndex, nextEndIndex).map((page) => page.index)

      setVisiblePages((prev) => [...prev, ...newVisiblePages])
      setIsLoadingMore(false)
    }, 300)
  }, [isLoadingMore, visiblePages.length, totalPages, pageData, pageSize])

  // 滚动到底部检测
  const handleScroll = useCallback(
    (e: any) => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
      const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0
      const clientHeight = window.innerHeight || document.documentElement.clientHeight || 0

      // 当滚动到距离底部100px时开始加载更多
      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMorePages()
      }
    },
    [loadMorePages]
  )

  // 使用节流优化滚动事件
  const throttledHandleScroll = useMemo(() => throttle(handleScroll, 200), [handleScroll])

  async function fetchMarkdownData(downloadUrl: string, contextType?: PostDataTypes.ContextType) {
    const res = await fetch(downloadUrl)
    const json = await res.json()

    let pageData = json.pages as PageData[]

    pageData?.forEach((page) => {
      const markdownString = page.markdown
      // 页面空白时，mistral 模型会自动补充内容，目前看都是量子力学相关的内容。这里需要过滤掉
      if (markdownString.includes('quantum mechanics')) {
        page.markdown = ''
      }
      // 过滤异常解析内容
      if (
        markdownString.includes(
          '...............................................................................................................................................................................................................'
        )
      ) {
        page.markdown = ''
      }
    })

    if (contextType) {
      const context = await getMarkdownContext(pageData, downloadUrl, contextType)
      proPdfTranslateHelper.contextType = contextType
      proPdfTranslateHelper.markdownContext = context
      console.log('@@@context:', contextType, context)
    }

    return pageData
  }

  async function fetchMarkdownFileUrl(params: {
    downloadUrl: string
    mdFileId: number
    fromHistory?: boolean
    start?: number
    end?: number
    contextType?: PostDataTypes.ContextType
  }) {
    const { downloadUrl, mdFileId, fromHistory, start, end, contextType } = params
    // if (fromHistory) {
    //   setHistoryLoading(true)
    //   await new Promise((resolve) => setTimeout(resolve, 1000))
    // }

    // const res = await fetch(downloadUrl)
    // const json = await res.json()

    const pageDataServer = await fetchMarkdownData(downloadUrl, contextType)

    // console.log('@@@downloadUrl:', downloadUrl)
    // console.log('@@@success:', json)
    let pageData = pageDataServer
    if (start !== undefined && end !== undefined) {
      // console.log('@@@start:', start)
      // console.log('@@@end:', end)
      if (start === end) {
        // 如果start和end一样，则只取start对应的页
        const page = pageData.find((item) => item.index === start)
        if (page) {
          pageData = [page]
        } else {
          pageData = []
        }
      } else {
        // 如果start和end不一样，则取start到end之间的页
        const startIndex = pageData.findIndex((item) => item.index === start)
        const endIndex = pageData.findIndex((item) => item.index === end)
        if (startIndex !== -1 && endIndex !== -1) {
          pageData = pageData.slice(startIndex, endIndex + 1)
        } else {
          pageData = []
        }
      }
    }

    const detectLangInfo = detectMarkdownLang(pageData)
    proPdfTranslateHelper.detectLangInfo = detectLangInfo

    // 将图片id替换为index-id，避免id重复
    replaceImageId(pageData)

    // console.log('@@@pageData:', pageData)
    setPageData(pageData)

    // 设置分页信息
    setTotalPages(pageData.length)

    // 初始显示前20页
    const initialVisiblePages = pageData.slice(0, pageSize).map((page) => page.index)
    setVisiblePages(initialVisiblePages)

    setShowBlurArea(false)
    setUploadingPdf(false)

    setTimeout(() => {
      setMarkdownFileId(mdFileId)
      setCurState('success')
      setParsePdfPercent(100)
      setParingPdf2markdown(false)
    }, 100)

    return pageData
  }

  function detectMarkdownLang(pageData: PageData[]) {
    const temp = pageData.slice(0, 2)
    const tempMarkdown = commonUtils.cleanMarkdownText(temp.map((item) => item.markdown).join(''))
    const detectLang = commonUtils.standardizeLocale(detect(tempMarkdown))
    if (detectLang) {
      const name = commonUtils.getLanguageDisplayNameByLocale(detectLang)
      return {
        lang: detectLang,
        name: name || detectLang,
      }
    }

    return null
  }

  async function getMarkdownContext(
    pageData: PageData[],
    downloadUrl: string,
    contextType: PostDataTypes.ContextType
  ) {
    if (contextType === 'close') {
      return ''
    }

    const contextKey = `${contextType}-${downloadUrl}`
    const context = userService.getPdfProContext(contextKey)
    if (context) {
      return context
    }

    const mainText = pageData
      .slice(0, 30)
      .map((item) => item.markdown)
      .join('')

    const text = commonUtils.truncateString(mainText, 20 * 1024)

    try {
      const res = await api.pdf.contextAnalysis({
        pageText: text,
        mode: contextType,
      })

      const contextStr = JSON.stringify(res.data)
      userService.savePdfProContext(contextKey, contextStr)

      return contextStr
    } catch (error) {
      return ''
    }
  }

  async function pdf2MarkdownSuccess(params: {
    downloadUrl: string
    fileId: number
    mdFileId: number
    display: boolean
    decryCount: number
    start: number
    end: number
    contextType: PostDataTypes.ContextType
    glossaryMode: TranslateServiceType.GlossaryMode
  }) {
    const {
      downloadUrl,
      fileId,
      mdFileId,
      display,
      decryCount,
      start,
      end,
      contextType,
      glossaryMode,
    } = params

    // 在加载文件之前，先尝试恢复之前保存的设置
    // 注意：这段逻辑是必要的，因为用户可能重新上传同一个文件（fileId相同），需要恢复之前的设置
    const savedSettings = userService.getPdfProFileSettings(fileId)
    let finalContextType = contextType
    if (savedSettings) {
      // 恢复翻译模型
      userService.pdfProTranslateModel = savedSettings.model
      proPdfTranslateHelper.setModel(savedSettings.model)

      // 恢复语言设置
      const saveSourceLang = commonUtils.standardizeLocale(savedSettings.sourceLang)
      if (commonUtils.isAutoLanguage(saveSourceLang)) {
        userService.pdfProSourceLang = TranslateServiceConsts.DEFAULT_TRANSLATE_SOURCE_LANG
      } else {
        userService.pdfProSourceLang = saveSourceLang
      }

      userService.pdfProTargetLang = savedSettings.targetLang

      // 恢复样式设置
      setStyleDefaultValue(savedSettings.styleType)

      // 恢复图片翻译模式
      setImageTranslateMode(savedSettings.imageTranslateMode)

      // 恢复术语表设置
      proPdfTranslateHelper.changeGlossaryMode(savedSettings.glossaryMode)

      // 恢复语境设置
      if (savedSettings.contextType) {
        finalContextType = savedSettings.contextType
      }

      // 原文和译文大小现在使用全局配置，不需要从文件设置中恢复
    }

    try {
      await fetchMarkdownFileUrl({
        downloadUrl,
        start,
        end,
        mdFileId,
        contextType: finalContextType,
      })

      try {
        await api.pdf.updateUploadStatus({
          fileId,
          decry: !display,
          decryCount,
          source: 'pdf_pro',
        })
      } catch (error) {
        toastController.error(commonUtils.formatErrorMsg(error))
      }

      console.log('save final config:', {
        fileId,
        model: proPdfTranslateHelper.model,
        sourceLang: userService.pdfProSourceLang,
        targetLang: userService.pdfProTargetLang,
        styleType: styleDefaultValue,
        imageTranslateMode: imageTranslateMode,
        contextType: finalContextType,
        glossaryMode,
      })

      // 保存当前文件的翻译设置（使用恢复后的值）
      saveFileSettings(fileId, {
        model: proPdfTranslateHelper.model,
        sourceLang: userService.pdfProSourceLang,
        targetLang: userService.pdfProTargetLang,
        styleType: styleDefaultValue,
        imageTranslateMode: imageTranslateMode,
        contextType: finalContextType,
        glossaryMode,
      })

      userService.updateUserInfo()
    } catch (error) {
      console.error(error)
      if (error.name === 'AbortError') {
        // toastController.warning('取消文件上传')
        console.log('@@@取消文件上传')
      } else {
        toastController.error(
          commonUtils.formatErrorMsg(error, '获取文件内容失败，请稍后重试或联系客服')
        )
      }
    }
  }

  async function createPdf2markdownTask({
    fileId,
    fileName,
    decryCount,
    display,
    numPages,
    start,
    end,
    contextType,
    glossaryMode,
  }: {
    fileId: number
    fileName: string
    decryCount: number
    display: boolean
    numPages: number
    start: number
    end: number
    contextType: PostDataTypes.ContextType
    glossaryMode: TranslateServiceType.GlossaryMode
  }) {
    try {
      setShowBlurArea(true)
      setParingPdf2markdown(true)
      setUploadPdfPercent(100)
      setParsePdfPercent(0)
      await new Promise((resolve) => setTimeout(resolve, 100))
      pdf2markdownController.current = new AbortController()
      let pages = [start, end]
      // 如果选中的页数和总页数一样，设置pages为空数组
      if (end - start + 1 === numPages) {
        pages = []
      }
      const { statusId } = await api.pdf.createPdf2markdownTask(
        fileId,
        numPages,
        pages,
        pdf2markdownController.current
      )
      curPdf2MarkdownFileId.current = fileId
      checkPdf2MarkdownStatus(fileId, statusId, async (status, mdFileId, downloadUrl, err) => {
        if (status === 'error') {
          setShowBlurArea(false)
          setParingPdf2markdown(false)
          setParsePdfError(true)
          toastController.error(err)
        } else if (status === 'success') {
          pdf2MarkdownSuccess({
            downloadUrl: downloadUrl!,
            fileId,
            mdFileId,
            display,
            decryCount,
            start,
            end,
            contextType,
            glossaryMode,
          })
        } else if (status === 'running') {
          if (parsePdfPercent < 98) {
            setParsePdfPercent((prev) => {
              const random = parseFloat((Math.random() * 2).toFixed(2))
              const result = prev + random
              if (result > 98) {
                return 98
              }
              return result
            })
          }
        } else {
          setParingPdf2markdown(false)
          setShowBlurArea(false)
          setParsePdfPercent(0)
          setUploadPdfPercent(0)
          toastController.error('文件转换失败，请稍后重试或联系客服-2')
        }
      })
    } catch (error) {
      setParingPdf2markdown(false)
      setShowBlurArea(false)
      setParsePdfPercent(0)
      setUploadPdfPercent(0)
      toastController.error(
        commonUtils.formatErrorMsg(error, '文件转换失败，请稍后重试或联系客服-1')
      )
    }
  }

  function resetState() {
    setShowBlurArea(false)
    setUploadingPdf(false)
    setUploadPdfPercent(0)
    setParsePdfPercent(0)
    setParingPdf2markdown(false)
    setParsePdfError(false)
    setCurState('normal')
    setMarkdownContent('')
    setPageData([])
    // 重置图片翻译状态
    resetImageTranslation()
  }

  async function fetchFileUrlFromHistory(params: {
    pdfFileId: number
    mdFileId: number
    fileName: string
    pages: number[]
    pageNum: number
  }) {
    const { pdfFileId, mdFileId, fileName, pages, pageNum } = params
    setFileName(fileName)
    setPdfFileId(pdfFileId)
    setMarkdownFileId(mdFileId)
    // 尝试恢复文件的翻译设置
    const savedSettings = userService.getPdfProFileSettings(pdfFileId)
    console.log('@@@savedSettings:', savedSettings)
    let contextType: PostDataTypes.ContextType | undefined = undefined
    if (savedSettings) {
      // 恢复翻译模型
      userService.pdfProTranslateModel = savedSettings.model
      proPdfTranslateHelper.setModel(savedSettings.model)

      // 恢复语言设置
      const saveSourceLang = commonUtils.standardizeLocale(savedSettings.sourceLang)
      if (commonUtils.isAutoLanguage(saveSourceLang)) {
        userService.pdfProSourceLang = TranslateServiceConsts.DEFAULT_TRANSLATE_SOURCE_LANG
      } else {
        userService.pdfProSourceLang = saveSourceLang
      }

      userService.pdfProTargetLang = savedSettings.targetLang

      // 恢复样式设置
      setStyleDefaultValue(savedSettings.styleType)

      // 恢复图片翻译模式
      setImageTranslateMode(savedSettings.imageTranslateMode)

      // 恢复术语表设置
      proPdfTranslateHelper.changeGlossaryMode(savedSettings.glossaryMode)

      // 原文和译文大小现在使用全局配置，不需要从文件设置中恢复

      // 恢复语境设置
      contextType = savedSettings.contextType
    }

    try {
      setHistoryLoading(true)
      const { downloadUrl } = await api.pdf.getFileUrl(mdFileId)
      if (downloadUrl) {
        if (pages && pages.length > 0) {
          const start = pages[0]
          const end = pages[pages.length - 1]
          // console.log('@@@start:', start)
          // console.log('@@@end:', end)
          await fetchMarkdownFileUrl({
            downloadUrl,
            mdFileId,
            fromHistory: true,
            start,
            end,
            contextType,
          })
        } else {
          await fetchMarkdownFileUrl({
            downloadUrl,
            mdFileId,
            fromHistory: true,
            contextType,
          })
        }
      } else {
        toastController.error('获取历史数据失败,请稍后重试或联系客服-1')
        setHistoryLoading(false)
      }
    } catch (error) {
      console.error(error)
      toastController.error(
        commonUtils.formatErrorMsg(error, '获取历史数据失败,请稍后重试或联系客服-2')
      )
      setHistoryLoading(false)
    }
  }

  async function checkPdf2MarkdownStatus(
    fileId: number,
    statusId: string,
    callback?: (
      status: ServerDataTypes.PDF.Pdf2HtmlStatus | 'error',
      mdFileId: number,
      downloadUrl?: string,
      err?: string
    ) => void
  ) {
    setParingPdf2markdown(true)
    let status: ServerDataTypes.PDF.Pdf2HtmlStatus | 'error' = 'no_running'
    let errMsg = '转换进度查询失败，请稍后重试或联系客服'
    let mdFileId = 0
    let downloadUrl = ''
    try {
      checkPdf2MarkdownController.current = new AbortController()
      const {
        status: _status,
        downloadUrl: _downloadUrl,
        mdFileId: _mdFileId,
      } = await api.pdf.checkPdf2markdownStatus(
        fileId,
        statusId,
        checkPdf2MarkdownController.current
      )
      setMarkdownFileId(mdFileId)
      status = _status
      mdFileId = _mdFileId
      downloadUrl = _downloadUrl
    } catch (error) {
      status = 'error'
      errMsg = commonUtils.formatErrorMsg(error, '转换进度查询失败，请稍后重试或联系客服')
    }
    if (status === 'running') {
      if (!curPdf2MarkdownFileId.current) {
        setParingPdf2markdown(false)
        return
      }
      checkPdf2MarkdownStatusTimer.current = setTimeout(() => {
        checkPdf2MarkdownStatus(fileId, statusId, callback)
      }, 1000)
    } else {
      // setParingPdf2markdown(false)
    }
    callback?.(status, mdFileId, downloadUrl, errMsg)
  }

  function handleCancelUpload() {
    if (xhr.current) {
      xhr.current.abort()
    }
    setUploadingPdf(false)
    setUploadPdfPercent(0)
    setParsePdfPercent(0)
    setShowBlurArea(false)
    setParingPdf2markdown(false)
    curPdf2MarkdownFileId.current = null

    clearTimeout(checkPdf2MarkdownStatusTimer.current)
    if (uploadSignUrlController.current) {
      uploadSignUrlController.current.abort()
    }

    if (updateUploadController.current) {
      updateUploadController.current.abort()
    }
    if (pdf2markdownController.current) {
      pdf2markdownController.current.abort()
    }
    if (checkPdf2MarkdownController.current) {
      checkPdf2MarkdownController.current.abort()
    }
  }

  function handleTabClick(
    tab:
      | 'pdf'
      | 'pdfPro'
      | 'img'
      | 'online'
      | 'translate'
      | 'guide'
      | 'select'
      | 'learn'
      | 'word'
      | 'pdfUltra'
  ) {
    if (tab === 'pdfPro') {
      routerService.openNewTab('pdf_pro')
      return
    }

    if (tab === 'pdf') {
      routerService.openNewTab('pdf')
      return
    }

    if (tab === 'img') {
      routerService.openNewTab('pdf', { tab: 'img' })
      return
    }

    if (tab === 'online') {
      routerService.openNewTab('pdf', { tab: 'online' })
      return
    }

    if (tab === 'guide') {
      routerService.openNewTab('welcome')
      return
    }

    if (tab === 'translate' || tab === 'select' || tab === 'learn') {
      routerService.openNewTab('home', { content: tab })
      return
    }

    if (tab === 'word') {
      routerService.openNewTab('word')
      return
    }

    if (tab === 'pdfUltra') {
      routerService.openNewTab('pdf_ultra')
      return
    }
  }

  // 获取未翻译的图片数量
  const getUntranslatedImageCount = () => {
    const container = document.querySelector('#pdf-markdown-content')
    if (!container) return 0

    const allImages = container.querySelectorAll('img[id]')
    let untranslatedCount = 0

    allImages.forEach((img) => {
      const imageId = img.id
      if (!translatedImages.current.has(imageId)) {
        untranslatedCount++
      }
    })

    return untranslatedCount
  }

  // 自动翻译剩余图片
  const autoTranslateRemainingImages = async (isDownload: boolean = false) => {
    const container = document.querySelector('#pdf-markdown-content')
    if (!container) return

    const allImages = container.querySelectorAll('img[id]')
    const untranslatedImages: { imageId: string; imageSrc: string }[] = []

    // 收集所有未翻译的图片
    allImages.forEach((img) => {
      const imageId = img.id
      const imageSrc = (img as HTMLImageElement).src

      if (!translatedImages.current.has(imageId) && !translatingImages.current.has(imageId)) {
        untranslatedImages.push({ imageId, imageSrc })
      }
    })

    // 使用节流逻辑，每次最多处理3张图片
    const batchSize = 3
    const delay = 100 // 每批之间的延迟时间（毫秒）

    for (let i = 0; i < untranslatedImages.length; i += batchSize) {
      const batch = untranslatedImages.slice(i, i + batchSize)
      const translatePromises = batch.map(({ imageId, imageSrc }) => {
        if (isDownload) {
          return new Promise<string>((resolve) => {
            fetchTranslateImage(imageSrc)
              .then((dataUrl) => {
                const imgEl = document.getElementById(imageId) as HTMLImageElement | null
                if (imgEl) {
                  imgEl.src = dataUrl
                }
                resolve(dataUrl)
              })
              .catch((err) => {
                resolve(imageSrc)
              })
          })
        } else {
          return translateImage(imageId, imageSrc, true).then((dataUrl) => {
            const imgEl = document.getElementById(imageId) as HTMLImageElement | null
            if (imgEl && dataUrl) {
              imgEl.src = dataUrl
            }
            return dataUrl
          })
        }
      })

      // 并行处理当前批次
      await Promise.all(translatePromises)

      // 如果不是最后一批，则等待一段时间再处理下一批
      if (i + batchSize < untranslatedImages.length) {
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }

  async function exportToHtml() {
    if (visiblePages.length === pageData.length || pageData.length <= PAGE_SIZE_THRESHOLD) {
      exportToHtmlFromLocal()
    } else {
      // 从服务器下载html
      downloadFromServer('html')
    }
  }

  async function exportToHtmlFromLocal() {
    setStartDownload(true)
    await new Promise((resolve) => setTimeout(resolve, 1000))
    // 检查并加载未渲染的页面
    await loadUnrenderedPagesIfNeeded()

    // 检查是否开启了自动翻译模式
    if (imageTranslateMode === 'auto') {
      const untranslatedCount = getUntranslatedImageCount()

      if (untranslatedCount > 0) {
        const userQuota = userService.remainImageTranslateCount
        const canTranslateAll = userQuota >= untranslatedCount

        const result = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: '导出确认',
            content: canTranslateAll
              ? `检测到您开启了图片自动翻译，导出时将自动翻译剩余的 ${untranslatedCount} 张图片，将消耗 ${untranslatedCount} 张图片翻译额度。是否继续？`
              : `检测到您开启了图片自动翻译，但您的图片翻译额度（剩余${userQuota}张）不足以翻译所有剩余图片（${untranslatedCount}张）。只能翻译其中${userQuota}张图片，其余图片将保持原图状态。是否继续？`,
            okText: '继续导出',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          })
        })

        if (result) {
          // 自动翻译剩余图片
          await autoTranslateRemainingImages()
        }
      }
    }

    try {
      const pdfContainer = document.querySelector('#pdf-markdown-content') as HTMLElement
      let renderType: RenderType = 'parallel'
      if (styleDefaultValue === 'onlyTranslation') {
        renderType = 'translation'
      } else if (styleDefaultValue === 'dualGrey') {
        renderType = 'parallel'
      } else {
        renderType = 'original'
      }
      await proPdfTranslateHelper.doTranslateAll('pdf', [pdfContainer], renderType, (progress) => {
        setDownloadPercent(Math.floor((progress.percentage / 100) * 100))
      })
      await userService.updateUserInfo()
      setStartDownload(false)
      setDownloadPercent(0)
      // 让渲染完成
      await new Promise((resolve) => setTimeout(resolve, 1000))

      const container = document.querySelector('#pdf-container')!
      const outerHtml = container.outerHTML

      const styles = [
        ...document.head.querySelectorAll('style'),
        ...document.querySelectorAll('link[rel="stylesheet"]'),
      ]
        .map((m) => m.outerHTML)
        .join(`\n`)

      const lang = userService.pdfProTargetLang
      const resultHtml = `<!DOCTYPE html>
                  <html lang="${lang}" >
                  <head>
                  <meta charset="UTF-8">
                  <base href="${window.location.origin}"></base>
                  ${styles}
                  </head>
                  ${outerHtml}
                  </html>
              `
      const blob = new Blob([resultHtml], {
        type: 'text/html;charset=utf-8',
      })

      const n = document.createElement('a')
      n.href = URL.createObjectURL(blob)

      const name = generateFileName(fileName, 'html')
      n.download = name
      document.body.appendChild(n)
      n.click()
      document.body.removeChild(n)
    } catch (error) {
      console.error('export html 翻译剩余元素时出错:', error)
    } finally {
      setStartDownload(false)
      setDownloadPercent(0)
    }
  }

  function generateFileName(fileName: string, suffix: 'pdf' | 'html') {
    const nameArr = fileName.split('.')

    let name = `${fileName}-会译.${suffix}`
    if (nameArr.length > 1) {
      nameArr.pop()
      name = `${nameArr.join('.')}-会译.${suffix}`
    }

    return name
  }

  const startPrintPDF = useReactToPrint({
    documentTitle: generateFileName(fileName, 'pdf'),
    content: () => document.querySelector('#pdf-container')!,
    onAfterPrint: () => {},
  })

  async function exportToPdf() {
    if (visiblePages.length === pageData.length || pageData.length <= PAGE_SIZE_THRESHOLD) {
      downloadPdfFromLocal()
    } else {
      // 从服务器下载pdf
      downloadFromServer('pdf')
    }
  }

  function exportAction(action: 'pdf' | 'html') {
    if (action === 'pdf') {
      exportToPdf()
    } else {
      exportToHtml()
    }
  }

  /**
   * 检查并加载未渲染的页面
   * @returns Promise<boolean> - true表示需要加载并已完成加载，false表示不需要加载
   */
  async function loadUnrenderedPagesIfNeeded(): Promise<boolean> {
    const visiblePagesCount = visiblePages.length
    const totalPagesLength = pageData.length
    const result = totalPagesLength - visiblePagesCount

    // 大于0，说明有未加载的页面
    if (result > 0) {
      // 标记为下载模式
      isLocalDownloadAction.current = true

      // 重置计数器
      // loadedPageCount.current = 0
      expectedPageCount.current = totalPagesLength

      // 设置显示所有页面
      setVisiblePages(pageData.slice(0, totalPagesLength).map((page) => page.index))

      // 等待所有页面渲染完成
      await new Promise<void>((resolve) => {
        pendingPageLoadResolve.current = resolve

        // 设置超时保护
        setTimeout(() => {
          if (pendingPageLoadResolve.current) {
            console.warn('等待页面加载超时，继续执行')
            pendingPageLoadResolve.current()
            pendingPageLoadResolve.current = null
          }
        }, 30000)
      })

      // 重置下载模式标记
      isLocalDownloadAction.current = false

      return true // 表示进行了加载操作
    }

    return false // 表示不需要加载
  }

  async function downloadPdfFromLocal() {
    setStartDownload(true)

    await new Promise((resolve) => setTimeout(resolve, 1000))
    // 检查并加载未渲染的页面
    await loadUnrenderedPagesIfNeeded()

    // 检查是否开启了自动翻译模式
    if (imageTranslateMode === 'auto') {
      const untranslatedCount = getUntranslatedImageCount()
      if (untranslatedCount > 0) {
        const userQuota = userService.remainImageTranslateCount
        const canTranslateAll = userQuota >= untranslatedCount
        const result = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: '导出确认',
            content: canTranslateAll
              ? `检测到您开启了图片自动翻译，导出时将自动翻译剩余的 ${untranslatedCount} 张图片，将消耗 ${untranslatedCount} 张图片翻译额度。是否继续？`
              : `检测到您开启了图片自动翻译，但您的图片翻译额度（剩余${userQuota}张）不足以翻译所有剩余图片（${untranslatedCount}张）。只能翻译其中${userQuota}张图片，其余图片将保持原图状态。是否继续？`,
            okText: '继续导出',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          })
        })
        if (result) {
          // 自动翻译剩余图片
          setStartDownload(true)
          await autoTranslateRemainingImages()
        }
      }
    }

    try {
      const container = document.querySelector('#pdf-markdown-content') as HTMLElement
      let renderType: RenderType = 'parallel'
      if (styleDefaultValue === 'onlyTranslation') {
        renderType = 'translation'
      } else if (styleDefaultValue === 'dualGrey') {
        renderType = 'parallel'
      } else {
        renderType = 'original'
      }
      const start = performance.now()
      await proPdfTranslateHelper.doTranslateAll('pdf', [container], renderType, (progress) => {
        setDownloadPercent(Math.floor((progress.percentage / 100) * 100))
      })
      const end = performance.now()
      console.log('@翻译时间:', (end - start) / 1000, '秒')
      await userService.updateUserInfo()
      setStartDownload(false)
      setDownloadPercent(0)
      await new Promise((resolve) => setTimeout(resolve, 400))
      startPrintPDF()
    } catch (error) {
      console.error('print pdf 翻译剩余元素时出错:', error)
      setStartDownload(false)
      setDownloadPercent(0)
    }
  }

  async function downloadFromServer(action: 'pdf' | 'html') {
    const model = proPdfTranslateHelper.model.key
    const sourceLang = proPdfTranslateHelper.actualApplySourceLang
    const targetLang = userService.pdfProTargetLang
    const style = proPdfTranslateHelper.styleType
    const glossaryMode = proPdfTranslateHelper.glossaryMode
    const image = imageTranslateMode
    const contextType = proPdfTranslateHelper.contextType
    console.log('download info:', {
      action,
      model,
      sourceLang,
      targetLang,
      style,
      image,
      glossaryMode,
      contextType,
    })

    if (serverDownloading) {
      toastController.warning('正在下载中，请稍后再试')
      return
    }

    if (!markdownFileId) {
      toastController.error('markdownFileId不存在')
      return
    }

    setServerDownloading(true)

    // 按照每100页60秒的规则计算处理时间
    let minutes = Math.max(1, Math.ceil(((pageData.length / 100) * 60) / 60))

    if (proPdfTranslateHelper.model.version === 'free') {
      minutes = minutes * 3
    }

    setServerDownloadInfo({
      showDownloadModal: true,
      time: minutes,
      isAIModel: proPdfTranslateHelper.model.type === 'ai',
      isFreeModel: proPdfTranslateHelper.model.version === 'free',
    })

    // 使用 URLSearchParams 构建查询字符串
    const params = new URLSearchParams()
    if (action) {
      params.append('action', action)
    }
    if (markdownFileId) {
      params.append('fileId', String(markdownFileId))
    }
    if (model) {
      params.append('model', model)
    }
    if (style) {
      params.append('style', style)
    }
    if (sourceLang) {
      params.append('sourceLang', sourceLang)
    }
    if (targetLang) {
      params.append('targetLang', targetLang)
    }
    if (image) {
      params.append('image', image)
    }
    if (glossaryMode) {
      params.append('glossaryMode', glossaryMode)
    }
    if (contextType) {
      params.append('contextType', contextType)
    }
    if (originalTextScale !== 1) {
      params.append('originalTextScale', String(originalTextScale))
    }
    if (translationTextScale !== 1) {
      params.append('translationTextScale', String(translationTextScale))
    }

    const queryString = params.toString()
    const redirectUrl = queryString
      ? `${envHelper.webHost}${window.location.pathname}?${queryString}`
      : `${envHelper.webHost}${window.location.pathname}`

    let downloadStatusTimer: NodeJS.Timeout | null = null
    let pollAttemptCount = 0
    const MAX_POLL_ATTEMPTS = 300 // 最多轮询5分钟 (300次 * 1秒)

    const start = performance.now()

    const pollDownloadStatus = async (markdownFileId: number) => {
      // 清除之前的定时器
      if (downloadStatusTimer) {
        clearTimeout(downloadStatusTimer)
        downloadStatusTimer = null
      }

      pollAttemptCount++

      // 检查是否超过最大尝试次数
      // if (pollAttemptCount > MAX_POLL_ATTEMPTS) {
      //   // 清理定时器和状态
      //   if (downloadStatusTimer) {
      //     clearTimeout(downloadStatusTimer)
      //     downloadStatusTimer = null
      //   }

      //   pollAttemptCount = 0
      //   setServerDownloading(false)
      //   setShowServerDownloadModal(false)

      //   // 触发下载超时事件
      //   toastController.error('下载超时，请稍后重试')

      //   console.log('PDF下载状态检查超时')
      //   return
      // }

      try {
        const { status, downloadUrl } = await api.pdf.downloadPdfStatus(markdownFileId)
        if (status === 'running') {
          // 继续轮询
          downloadStatusTimer = setTimeout(() => {
            pollDownloadStatus(markdownFileId)
          }, 5000)
        } else if (status === 'success') {
          // 清理定时器和状态
          if (downloadStatusTimer) {
            clearTimeout(downloadStatusTimer)
            downloadStatusTimer = null
          }

          console.log('文件下载状态检查完成', downloadUrl)
          try {
            await commonUtils.downloadFileByUrl(downloadUrl, generateFileName(fileName, action))

            pollAttemptCount = 0
            setServerDownloading(false)
            setServerDownloadInfo({
              showDownloadModal: false,
              time: 0,
              isAIModel: false,
              isFreeModel: false,
            })
            // 触发下载成功事件
            toastController.success('文件下载完成')
            const end = performance.now()
            console.log('@下载时间:', (end - start) / 1000, '秒')
          } catch (error) {
            toastController.error('下载失败，请稍后重试')
            console.error('下载失败:', error)
            pollAttemptCount = 0
            setServerDownloading(false)
            setServerDownloadInfo({
              showDownloadModal: false,
              time: 0,
              isAIModel: false,
              isFreeModel: false,
            })
          }
        } else {
          //异常情况 清理定时器和状态
          if (downloadStatusTimer) {
            clearTimeout(downloadStatusTimer)
            downloadStatusTimer = null
          }

          pollAttemptCount = 0
          setServerDownloading(false)
          setServerDownloadInfo({
            showDownloadModal: false,
            time: 0,
            isAIModel: false,
            isFreeModel: false,
          })

          // 触发下载错误事件
          toastController.error('检查下载状态失败，请重试-1')
        }
      } catch (error) {
        console.error('检查下载状态失败:', error)

        // 如果是网络错误或临时错误，继续重试
        if (pollAttemptCount < 10) {
          // 前10次错误时继续重试
          downloadStatusTimer = setTimeout(() => {
            pollDownloadStatus(markdownFileId)
          }, 1000)
        } else {
          // 错误次数过多，停止轮询
          toastController.error('检查下载状态失败，请重试-2')
        }
      }
    }

    try {
      await api.pdf.downloadPdf({
        fileId: markdownFileId,
        fromLang: sourceLang,
        toLang: targetLang,
        printType: action,
        redirectUrl,
        totalPagesCount: pageData.length,
      })

      // 开始轮询检查下载状态
      pollDownloadStatus(markdownFileId)
    } catch (error) {
      setServerDownloading(false)
      setServerDownloadInfo({
        showDownloadModal: false,
        time: 0,
        isAIModel: false,
        isFreeModel: false,
      })
      toastController.error('启动下载失败，请重试')
      console.error('启动PDF下载失败:', error)
    }
  }

  // 第一个 useEffect - 初始化检查和数据加载
  useEffect(() => {
    const initializeFromUrl = async () => {
      const urlParams = {
        action: commonUtils.getQueryFromUrl('action', window.location.href),
        fileId: commonUtils.getQueryFromUrl('fileId', window.location.href),
        model: commonUtils.getQueryFromUrl('model', window.location.href),
        style: commonUtils.getQueryFromUrl('style', window.location.href),
        sourceLang: commonUtils.getQueryFromUrl('sourceLang', window.location.href),
        targetLang: commonUtils.getQueryFromUrl('targetLang', window.location.href),
        pageStart: commonUtils.getQueryFromUrl('pageStart', window.location.href),
        pageEnd: commonUtils.getQueryFromUrl('pageEnd', window.location.href),
        glossaryMode: commonUtils.getQueryFromUrl('glossaryMode', window.location.href),
        contextType: commonUtils.getQueryFromUrl('contextType', window.location.href),
        originalTextScale: commonUtils.getQueryFromUrl('originalTextScale', window.location.href),
        translationTextScale: commonUtils.getQueryFromUrl(
          'translationTextScale',
          window.location.href
        ),
      }

      console.log('@@URL parameters:', urlParams)

      // 检查必要参数
      let {
        action,
        fileId,
        model,
        style,
        sourceLang,
        targetLang,
        pageStart,
        pageEnd,
        glossaryMode,
        contextType,
      } = urlParams
      if (!action || !fileId || !model || !style || !sourceLang || !targetLang) {
        window.dispatchEvent(new CustomEvent('DOWNLOAD_ERROR', { detail: { message: '参数错误' } }))
        return
      }

      if (!userService.isLogin) {
        try {
          await userService.updateUserInfo()
        } catch (error) {
          window.dispatchEvent(
            new CustomEvent('DOWNLOAD_ERROR', { detail: { message: '用户未登录-1' } })
          )
          return
        }

        if (!userService.isLogin) {
          window.dispatchEvent(
            new CustomEvent('DOWNLOAD_ERROR', { detail: { message: '用户未登录-2' } })
          )
          return
        }
      }

      try {
        // 获取文件数据
        const { downloadUrl } = await api.pdf.getFileUrl(Number(fileId))
        if (!downloadUrl) {
          window.dispatchEvent(
            new CustomEvent('DOWNLOAD_ERROR', { detail: { message: 'downloadUrl文件不存在' } })
          )
          return
        }

        // const res = await fetch(downloadUrl)
        // const json = await res.json()
        contextType = contextType ?? 'close'
        const pageDataServer = await fetchMarkdownData(
          downloadUrl,
          contextType as PostDataTypes.ContextType
        )

        // 设置模型
        const dataList = await userService.getLLMModelList('translate')
        const modelItem = dataList.find((item) => item.key === model)
        if (modelItem) {
          proPdfTranslateHelper.setModel(modelItem)
        } else {
          if (userService.vipVersion === 'free') {
            proPdfTranslateHelper.setModel(MODEL_STRATEGY_CONFIG['pdf'].freeModel)
          } else {
            proPdfTranslateHelper.setModel(MODEL_STRATEGY_CONFIG['pdf'].vipModel)
          }
        }

        // 设置样式和语言
        proPdfTranslateHelper.changeStyleType(style as TranslateServiceType.StyleType)
        proPdfTranslateHelper.changeGlossaryMode(glossaryMode as TranslateServiceType.GlossaryMode)
        userService.pdfProSourceLang = commonUtils.standardizeLocale(sourceLang)
        userService.pdfProTargetLang = targetLang

        // 设置原文和译文大小（全局配置），处理精度问题
        const originalScale = urlParams.originalTextScale
          ? roundToDecimal(parseFloat(urlParams.originalTextScale), 1)
          : 1
        const translationScale = urlParams.translationTextScale
          ? roundToDecimal(parseFloat(urlParams.translationTextScale), 1)
          : 1
        userService.savePdfProOriginalTextScale(originalScale)
        userService.savePdfProTranslationTextScale(translationScale)

        // 处理页面数据
        let pageData = pageDataServer

        if (!isNil(pageStart) && !isNil(pageEnd)) {
          pageData = pageData.slice(Number(pageStart), Number(pageEnd) + 1)
        }

        replaceImageId(pageData)
        setPageData(pageData)

        // 设置页面状态
        const totalLength = pageData.length
        setTotalPages(totalLength)
        setVisiblePages(pageData.slice(0, totalLength).map((page) => page.index))

        // 更新 UI 状态
        setOriginalTextScaleInput(String(originalScale))
        setTranslationTextScaleInput(String(translationScale))
        setShowBlurArea(false)
        setUploadingPdf(false)
        setCurState('success')
        setParsePdfPercent(100)
        setParingPdf2markdown(false)

        console.log('页面数据加载完成:', pageData)

        // 标记为下载操作
        isServerDownloadAction.current = true
      } catch (error) {
        const msg = commonUtils.formatErrorMsg(error, '初始化失败')
        console.error('初始化失败:', msg)
        window.dispatchEvent(new CustomEvent('DOWNLOAD_ERROR', { detail: { message: msg } }))
      }
    }

    initializeFromUrl()
  }, [])

  // 第二个 useEffect - 处理翻译逻辑
  useEffect(() => {
    const executeTranslation = async () => {
      if (curState !== 'success' || !isServerDownloadAction.current) return

      const urlParams = {
        action: commonUtils.getQueryFromUrl('action', window.location.href),
        fileId: commonUtils.getQueryFromUrl('fileId', window.location.href),
        model: commonUtils.getQueryFromUrl('model', window.location.href),
        style: commonUtils.getQueryFromUrl('style', window.location.href),
        sourceLang: commonUtils.getQueryFromUrl('sourceLang', window.location.href),
        targetLang: commonUtils.getQueryFromUrl('targetLang', window.location.href),
        pageStart: commonUtils.getQueryFromUrl('pageStart', window.location.href),
        pageEnd: commonUtils.getQueryFromUrl('pageEnd', window.location.href),
        glossaryMode: commonUtils.getQueryFromUrl('glossaryMode', window.location.href),
        contextType: commonUtils.getQueryFromUrl('contextType', window.location.href),
        originalTextScale: commonUtils.getQueryFromUrl('originalTextScale', window.location.href),
        translationTextScale: commonUtils.getQueryFromUrl(
          'translationTextScale',
          window.location.href
        ),
      }

      try {
        // 隐藏不必要的 UI 元素
        const elementsToHide = [
          { selector: '.settingHeader', name: '设置头部' },
          { selector: '.proHeader', name: '专业头部' },
        ]

        elementsToHide.forEach(({ selector, name }) => {
          const element = document.querySelector(selector) as HTMLElement
          if (element) {
            element.style.display = 'none'
            console.log(`已隐藏${name}`)
          }
        })

        // 调整容器样式
        const pdfProContainer = document.querySelector('.pdfProContainer') as HTMLElement
        if (pdfProContainer) {
          pdfProContainer.style.paddingTop = '0'
        }

        const originalScale = urlParams.originalTextScale
          ? roundToDecimal(parseFloat(urlParams.originalTextScale), 1)
          : 1
        const translationScale = urlParams.translationTextScale
          ? roundToDecimal(parseFloat(urlParams.translationTextScale), 1)
          : 1

        // 执行文本翻译
        const pdfMarkdownContent = document.querySelector('#pdf-markdown-content') as HTMLElement
        if (pdfMarkdownContent) {
          // 设置 CSS 变量到容器元素，影响所有子元素
          pdfMarkdownContent.style.setProperty('--original-text-scale', String(originalScale))
          pdfMarkdownContent.style.setProperty('--translation-text-scale', String(translationScale))

          // 根据样式类型设置渲染类型
          const styleType = commonUtils.getQueryFromUrl('style', window.location.href)
          let renderType: RenderType = 'parallel'
          if (styleType === 'onlyTranslation') {
            renderType = 'translation'
          } else if (styleType === 'dualGrey') {
            renderType = 'parallel'
          } else {
            renderType = 'original'
          }
          await proPdfTranslateHelper.doTranslateAll('pdf', [pdfMarkdownContent], renderType)
          console.log('文本翻译完成')
        }

        // 检查是否需要自动翻译图片
        const image = commonUtils.getQueryFromUrl('image', window.location.href)
        if (image === 'auto') {
          await autoTranslateRemainingImages(true)
          console.log('图片翻译完成')
        }

        // 替换 setTimeout 的部分
        const waitForRenderComplete = () => {
          return new Promise<void>((resolve) => {
            // 等待下一帧渲染完成
            requestAnimationFrame(() => {
              // 如果支持 requestIdleCallback，等待浏览器空闲
              if (typeof requestIdleCallback !== 'undefined') {
                requestIdleCallback(
                  () => {
                    resolve()
                  },
                  { timeout: 3000 }
                ) // 最多等待3秒
              } else {
                // 降级方案：再等一帧
                requestAnimationFrame(() => {
                  resolve()
                })
              }
            })
          })
        }

        // 替换现有的 setTimeout
        await waitForRenderComplete()
        window.dispatchEvent(new CustomEvent('DOWNLOAD_DONE'))
        console.log('翻译流程完全结束')
      } catch (error) {
        console.error('翻译执行失败:', error)
        window.dispatchEvent(
          new CustomEvent('DOWNLOAD_ERROR', { detail: { message: '翻译执行失败' } })
        )
      }
    }

    executeTranslation()
  }, [curState])

  // 滚动监听，用于自动翻译
  useEffect(() => {
    if (curState === 'success' && imageTranslateMode === 'auto') {
      let timeoutId: NodeJS.Timeout | null = null
      const handleScroll = () => {
        if (timeoutId) clearTimeout(timeoutId)
        timeoutId = setTimeout(checkAutoTranslate, 300)
      }
      window.addEventListener('scroll', handleScroll)
      return () => {
        window.removeEventListener('scroll', handleScroll)
        if (timeoutId) clearTimeout(timeoutId)
      }
    }
  }, [checkAutoTranslate, curState, imageTranslateMode])

  // 使用useEffect监听window滚动事件
  useEffect(() => {
    if (curState === 'success') {
      window.addEventListener('scroll', throttledHandleScroll, { passive: true })
    }

    return () => {
      if (curState === 'success') {
        window.removeEventListener('scroll', throttledHandleScroll)
      }
    }
  }, [throttledHandleScroll, curState])

  useEffect(() => {
    proPdfTranslateHelper.changeGlossaryMode(glossaryMode)
  }, [glossaryMode])

  // 同步输入框显示值与全局配置（当全局配置变化时）
  useEffect(() => {
    setOriginalTextScaleInput(String(originalTextScale))
  }, [originalTextScale])

  useEffect(() => {
    setTranslationTextScaleInput(String(translationTextScale))
  }, [translationTextScale])

  // 应用原文和译文大小调整 - 使用 CSS 变量影响布局空间
  useEffect(() => {
    if (curState !== 'success') return

    const container = document.querySelector('#pdf-markdown-content') as HTMLElement
    if (!container) return

    // 设置 CSS 变量到容器元素，影响所有子元素
    container.style.setProperty('--original-text-scale', String(originalTextScale))
    container.style.setProperty('--translation-text-scale', String(translationTextScale))

    return () => {
      // 清理时移除 CSS 变量
      container.style.removeProperty('--original-text-scale')
      container.style.removeProperty('--translation-text-scale')
    }
  }, [curState, originalTextScale, translationTextScale])

  return (
    <Worker workerUrl='https://assets.weibanzhushou.com/web/pt-web/pdfjs-dist%403.4.120.min.496b8e4d960f.js'>
      <div
        className={classNames(style.pdfPro, 'pdfProContainer', {
          [style.bgTransparent]: curState === 'success',
        })}
        ref={(el) => setDragAreaRef(el)}
      >
        {/* 顶栏 */}
        <ProHeader
          renderPage={curState}
          onJoinClick={() => {}}
          onTabClick={(tab) => {
            handleTabClick(tab)
          }}
          exportToHtml={() => {
            exportAction('html')
          }}
          exportToPdf={() => {
            exportAction('pdf')
          }}
        />

        {/* 自动翻译额度不足提示条 - 固定定位 */}
        {showImageQuotaAlert && (
          <div
            style={{
              position: 'fixed',
              top: '62px', // 紧贴在顶部导航栏下方
              left: '0',
              right: '0',
              zIndex: 1000,
              padding: '0 16px',
              backgroundColor: 'transparent',
              pointerEvents: 'none', // 让背景透明区域不阻挡点击
            }}
          >
            <Alert
              message='图片翻译额度不足，无法继续自动翻译图片，请升级会员获取更多额度'
              type='warning'
              showIcon
              closable
              onClose={() => setShowImageQuotaAlert(false)}
              action={
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span
                    style={{ color: '#1890ff', cursor: 'pointer' }}
                    onClick={() => {
                      userService.doPay({
                        wordPack: false,
                        from: 'pdf_img_auto_translate_alert',
                        version: userService.vipVersion === 'free' ? 'max' : 'ultra',
                        feature: 'pdfPro',
                      })
                    }}
                  >
                    升级会员
                  </span>
                  <span
                    style={{ color: '#666', cursor: 'pointer' }}
                    onClick={() => setShowImageQuotaAlert(false)}
                  >
                    我知道了
                  </span>
                </div>
              }
              style={{
                margin: '0',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                pointerEvents: 'all', // Alert 组件本身可以点击
              }}
            />
          </div>
        )}

        {/* 引导 */}
        {curState === 'normal' && (
          <Guide
            onLoadOnlinePdf={() => {}}
            onChooseFile={() => {
              handleChooseFile()
            }}
            onOpenHistoryModal={() => {
              setShowHistory(true)
            }}
          />
        )}

        {/* 下载的翻译loading */}
        {startDownload && (
          <Modal
            open={true}
            onCancel={() => {}}
            footer={null}
            centered
            closable={false}
            maskClosable={false}
          >
            <div className={style.downloadProgress}>
              正在下载中，请稍后...
              <Progress percent={downloadPercent} />
            </div>
          </Modal>
        )}

        {/* 中间状态,选页数和目标语言,点击翻译后,开始翻译 */}
        {curState === 'middle' && (
          <MiddleStatus
            onBack={() => {
              setCurState('normal')
              setFile(null)
              setPdfTotalPage(0)
            }}
            file={file!}
            totalPage={pdfTotalPage}
            styleDefaultValue={styleDefaultValue}
            glossaryMode={userService.pdfProGlossaryMode}
            onStyleChange={(style) => {
              setStyleDefaultValue(style)
            }}
            onGlossaryChange={(glossary) => {
              setGlossaryMode(glossary)
            }}
            onTranslateClick={(params: {
              start: number
              end: number
              targetLang: string
              model: ServerDataTypes.Model
              contextType: PostDataTypes.ContextType
              glossaryMode: TranslateServiceType.GlossaryMode
            }) => {
              startUploadFile({
                file: file!,
                totalPages: pdfTotalPage,
                start: params.start,
                end: params.end,
                targetLang: params.targetLang,
                model: params.model,
                contextType: params.contextType,
                glossaryMode: params.glossaryMode,
              })
            }}
          />
        )}

        {/* 拖拽区域，蒙层，上传进度条 */}
        {dragAreaRef && (
          <FullscreenDrag
            ref={fullscreenDragRef}
            disabled={curState !== 'normal'}
            dragAreaRef={dragAreaRef}
            uploadingPdf={uploadingPdf}
            paringPdf2Html={paringPdf2markdown}
            uploadPercent={(uploadPercent + parsePdfPercent) / 2}
            handleCancelUpload={() => {
              handleCancelUpload()
            }}
            handleDropFile={(file) => {
              afterChooseFile(file, true)
            }}
          />
        )}

        {/* 历史记录 */}
        {curState === 'normal' && (
          <UploadHistoryModal
            open={showHistory}
            currentFileId={pdfFileId}
            onPdfFileChanged={({ pdfFileId, fileId, fileName, pages, pageNum }) => {
              fetchFileUrlFromHistory({ pdfFileId, mdFileId: fileId, fileName, pages, pageNum })
            }}
            onClose={() => {
              setShowHistory(false)
            }}
          />
        )}

        {/* 从历史记录点击后的loading */}
        {curState === 'normal' && historyLoading && (
          <Spin className={style.loading} spinning={true} />
        )}

        {/* 渲染成功后，设置区域 */}
        {curState === 'success' && (
          <div className={classNames(style.settingHeader, 'settingHeader')}>
            <div className={classNames(style.item)}>
              <div className={classNames(style.title)}>
                <p className={classNames(style.text)}>翻译模型</p>
              </div>

              <div className={classNames(style.setting)}>
                <ModelPopover
                  apiFilter='translate'
                  currentModel={proPdfTranslateHelper.model}
                  onModelChange={(model) => {
                    modelChanged(model)
                  }}
                  onRenderCurrentItem={(item) => {
                    return (
                      <div className={classNames(style.valueWrap)}>
                        <img className={classNames(style.modelIcon)} src={item.icon} />
                        <p className={classNames(style.value)}>{item.name}</p>
                        <img
                          className={classNames(style.arrow)}
                          src={commonUtils.getStaticFile('online__arrow.png')}
                        />
                      </div>
                    )
                  }}
                />
              </div>
            </div>
            <div className={classNames(style.item, style.sourceLangItem)}>
              <div className={classNames(style.title)}>
                <p className={classNames(style.text)}>原文语言</p>
              </div>

              <div className={classNames(style.setting)}>
                <LanguagePopover
                  placement='bottom'
                  align={{
                    offset: [-90, 10],
                  }}
                  placeholder={'请输入搜索内容'}
                  emptyPlaceholder={'不支持当前语言'}
                  languageList={userService.sourceLanguageOptions}
                  value={userService.pdfProSourceLang}
                  onLanguageChange={(value) => {
                    languageChanged('source', value)
                  }}
                  getPopupContainer={(el) => el.parentElement!}
                  onLoadChildren={(languageItem) => {
                    return (
                      <div className={classNames(style.valueWrap)}>
                        <p className={classNames(style.value)}>
                          {languageItem.value === TranslateServiceConsts.AUTO_DETECT_LANG &&
                          proPdfTranslateHelper.detectLangInfo
                            ? `${proPdfTranslateHelper.detectLangInfo.name} (自动检测)`
                            : languageItem.label}
                        </p>
                        <img
                          className={classNames(style.arrow)}
                          src={commonUtils.getStaticFile('online__arrow.png')}
                        />
                      </div>
                    )
                  }}
                ></LanguagePopover>
              </div>
            </div>

            <div className={classNames(style.item, style.targetLangItem)}>
              <div className={classNames(style.title)}>
                <p className={classNames(style.text)}>目标语言</p>
              </div>

              <div className={classNames(style.setting)}>
                <LanguagePopover
                  placement='bottom'
                  align={{
                    offset: [-90, 10],
                  }}
                  placeholder={'请输入搜索内容'}
                  emptyPlaceholder={'不支持当前语言'}
                  languageList={userService.targetLanguageOptions}
                  value={userService.pdfProTargetLang}
                  onLanguageChange={(value) => {
                    languageChanged('target', value)
                  }}
                  getPopupContainer={(el) => el.parentElement!}
                  onLoadChildren={(languageItem) => {
                    return (
                      <div className={classNames(style.valueWrap)}>
                        <p className={classNames(style.value)}>{languageItem.label}</p>
                        <img
                          className={classNames(style.arrow)}
                          src={commonUtils.getStaticFile('online__arrow.png')}
                        />
                      </div>
                    )
                  }}
                ></LanguagePopover>
              </div>
            </div>

            {/* 更多配置按钮 */}
            <button
              className={classNames(style.moreConfigBtn)}
              onClick={() => setShowMoreConfigModal(true)}
            >
              更多配置
            </button>
          </div>
        )}

        {/* 渲染markdown内容 */}
        {curState === 'success' && (
          <PdfMarkdown
            pageData={pageData}
            imageTranslateMode={imageTranslateMode}
            onImageTranslate={toggleImageTranslation}
            // styleValue={styleDefaultValue}
            // currentPage={currentPage}
            visiblePages={visiblePages}
            isLoadingMore={isLoadingMore}
            onPageLoad={onPageLoad}
            onImageLoad={onImageLoad}
          />
        )}

        {/* 更多配置面板 */}
        <Modal
          title='更多配置'
          open={showMoreConfigModal}
          onCancel={() => setShowMoreConfigModal(false)}
          footer={null}
          width={500}
          centered
        >
          <div className={classNames(style.moreConfigModal)}>
            {/* 译文样式配置 */}
            <div className={classNames(style.configItem)}>
              <div className={classNames(style.configTitle)}>
                <span>译文样式</span>
              </div>
              <div className={classNames(style.configSetting)}>
                <SearchPopover
                  placement='bottom'
                  placeholder={'请输入搜索内容'}
                  emptyPlaceholder={'没有找到数据'}
                  align={{
                    offset: [-70, 10],
                  }}
                  searchEnable={false}
                  menuList={TranslateServiceConsts.STYLE_CONFIG_OPTIONS}
                  getPopupContainer={(el) => el.parentElement!}
                  value={styleDefaultValue}
                  onValueChange={(value) => {
                    styleChanged(value)
                  }}
                  onRenderItem={(menuItem) => {
                    return <p className={classNames(style.main)}>{menuItem.label}</p>
                  }}
                  onLoadChildren={(menuItem) => {
                    return (
                      <div className={classNames(style.valueWrap)}>
                        <p className={classNames(style.value)}>{menuItem.label}</p>
                        <img
                          className={classNames(style.arrow)}
                          src={commonUtils.getStaticFile('online__arrow.png')}
                        />
                      </div>
                    )
                  }}
                ></SearchPopover>
              </div>
            </div>

            {/* 图片翻译配置 */}
            <div className={classNames(style.configItem)}>
              <div className={classNames(style.configTitle)}>
                <span>图片翻译</span>
                <Tooltip
                  title={
                    <div style={{ lineHeight: '1.6' }}>
                      <div>可翻译 PDF 中的图片，会消耗图片翻译额度</div>
                      <div style={{ marginTop: '8px' }}>
                        <div>
                          <strong>手动翻译：</strong>点击图片右下角的翻译按钮
                        </div>
                        <div>
                          <strong>自动翻译：</strong>滚动到图片时会自动翻译
                        </div>
                      </div>
                    </div>
                  }
                  placement='bottomLeft'
                  overlayStyle={{ maxWidth: '300px' }}
                >
                  <QuestionCircleOutlined
                    style={{
                      fontSize: '15px',
                      cursor: 'pointer',
                      opacity: 0.6,
                      color: '#666',
                      marginLeft: '8px',
                    }}
                  />
                </Tooltip>
              </div>
              <div className={classNames(style.configSetting)}>
                <SearchPopover
                  placement='bottom'
                  placeholder={'请输入搜索内容'}
                  emptyPlaceholder={'没有找到数据'}
                  align={{
                    offset: [-70, 10],
                  }}
                  searchEnable={false}
                  menuList={[
                    { value: 'manual', label: '手动翻译' },
                    { value: 'auto', label: '自动翻译' },
                  ]}
                  getPopupContainer={(el) => el.parentElement!}
                  value={imageTranslateMode}
                  onValueChange={(value) => {
                    setImageTranslateMode(value as 'manual' | 'auto')

                    // 保存图片翻译模式设置
                    if (pdfFileId) {
                      saveFileSettings(pdfFileId, {
                        model: proPdfTranslateHelper.model,
                        sourceLang: userService.pdfProSourceLang,
                        targetLang: userService.pdfProTargetLang,
                        styleType: styleDefaultValue,
                        imageTranslateMode: value as 'manual' | 'auto',
                      })
                    }

                    // 关闭更多配置面板
                    setShowMoreConfigModal(false)
                  }}
                  onRenderItem={(menuItem) => {
                    return <p className={classNames(style.main)}>{menuItem.label}</p>
                  }}
                  onLoadChildren={(menuItem) => {
                    return (
                      <div className={classNames(style.valueWrap)}>
                        <p className={classNames(style.value)}>{menuItem.label}</p>
                        <img
                          className={classNames(style.arrow)}
                          src={commonUtils.getStaticFile('online__arrow.png')}
                        />
                      </div>
                    )
                  }}
                ></SearchPopover>
              </div>
            </div>

            {/* 原文大小调整配置 */}
            <div className={classNames(style.configItem)}>
              <div className={classNames(style.configTitle)}>
                <span>原文大小调整</span>
              </div>
              <div className={classNames(style.configSetting)}>
                <Input
                  type='number'
                  min={0.1}
                  max={10}
                  step={0.1}
                  value={originalTextScaleInput || String(originalTextScale)}
                  onChange={(e) => {
                    const inputValue = e.target.value
                    setOriginalTextScaleInput(inputValue)

                    // 如果输入值有效，更新全局配置
                    const numValue = parseFloat(inputValue)
                    if (!isNaN(numValue) && numValue >= 0.1 && numValue <= 10) {
                      const roundedValue = roundToDecimal(numValue, 1)
                      // 保存全局配置到浏览器缓存（会自动更新 userService，触发重新渲染）
                      userService.savePdfProOriginalTextScale(roundedValue)
                    }
                  }}
                  onBlur={(e) => {
                    const inputValue = e.target.value.trim()
                    // 如果为空或无效，设置为默认值 1
                    const numValue = parseFloat(inputValue)
                    if (!inputValue || isNaN(numValue) || numValue < 0.1 || numValue > 10) {
                      setOriginalTextScaleInput('1')
                      // 保存全局配置到浏览器缓存（会自动更新 userService，触发重新渲染）
                      userService.savePdfProOriginalTextScale(1)
                    } else {
                      // 确保显示值与实际值一致，处理精度问题
                      const roundedValue = roundToDecimal(numValue, 1)
                      setOriginalTextScaleInput(String(roundedValue))
                      // 保存全局配置到浏览器缓存（会自动更新 userService，触发重新渲染）
                      userService.savePdfProOriginalTextScale(roundedValue)
                    }
                  }}
                  style={{ width: '100px' }}
                  placeholder='1'
                />
                <span style={{ marginLeft: '8px', color: '#666' }}>倍</span>
              </div>
            </div>

            {/* 译文大小调整配置 */}
            <div className={classNames(style.configItem)}>
              <div className={classNames(style.configTitle)}>
                <span>译文大小调整</span>
              </div>
              <div className={classNames(style.configSetting)}>
                <Input
                  type='number'
                  min={0.1}
                  max={10}
                  step={0.1}
                  value={translationTextScaleInput || String(translationTextScale)}
                  onChange={(e) => {
                    const inputValue = e.target.value
                    setTranslationTextScaleInput(inputValue)

                    // 如果输入值有效，更新全局配置
                    const numValue = parseFloat(inputValue)
                    if (!isNaN(numValue) && numValue >= 0.1 && numValue <= 10) {
                      const roundedValue = roundToDecimal(numValue, 1)
                      // 保存全局配置到浏览器缓存（会自动更新 userService，触发重新渲染）
                      userService.savePdfProTranslationTextScale(roundedValue)
                    }
                  }}
                  onBlur={(e) => {
                    const inputValue = e.target.value.trim()
                    // 如果为空或无效，设置为默认值 1
                    const numValue = parseFloat(inputValue)
                    if (!inputValue || isNaN(numValue) || numValue < 0.1 || numValue > 10) {
                      setTranslationTextScaleInput('1')
                      // 保存全局配置到浏览器缓存（会自动更新 userService，触发重新渲染）
                      userService.savePdfProTranslationTextScale(1)
                    } else {
                      // 确保显示值与实际值一致，处理精度问题
                      const roundedValue = roundToDecimal(numValue, 1)
                      setTranslationTextScaleInput(String(roundedValue))
                      // 保存全局配置到浏览器缓存（会自动更新 userService，触发重新渲染）
                      userService.savePdfProTranslationTextScale(roundedValue)
                    }
                  }}
                  style={{ width: '100px' }}
                  placeholder='1'
                />
                <span style={{ marginLeft: '8px', color: '#666' }}>倍</span>
              </div>
            </div>
          </div>
        </Modal>

        {/* 对比模型弹窗 */}
        {uiController.compareDialogData && (
          <CompareTranslateDialog
            {...uiController.compareDialogData}
            currentModel={proPdfTranslateHelper.model}
            sourceLang={proPdfTranslateHelper.actualApplySourceLang}
            targetLang={userService.pdfProTargetLang}
            onClose={() => (uiController.compareDialogData = null)}
          />
        )}

        {/* 从服务器下载弹窗 */}
        <Modal
          title={`正在下载中,请勿刷新或关闭网页`}
          open={serverDownloadInfo.showDownloadModal}
          // onCancel={() => setShowServerDownloadModal(false)}
          footer={null}
          // closable={false}
          maskClosable={false}
          closeIcon={false}
          width={500}
          centered
        >
          <div className={classNames(style.serverDownloadModal)}>
            <p className={classNames(style.text)}>
              当前PDF页数较多，请耐心等待～预计{serverDownloadInfo.time}分钟后完成下载
              {serverDownloadInfo.isFreeModel ? <>（免费模型耗时更长）</> : ''}
              {serverDownloadInfo.isAIModel ? <>（AI 模型翻译更精准，但会增加耗时）</> : ''}
              <LoadingDot />
            </p>
            <div className={classNames(style.footer)}>
              <Button type='primary' onClick={() => routerService.openNewTab('pdf_pro')}>
                翻译更多PDF
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Worker>
  )
})
function replaceImageId(pageData: PageData[]) {
  pageData.forEach((page) => {
    const index = page.index
    let markdown = page.markdown
    const images = page.images

    images.forEach((image) => {
      const oldId = image.id
      image.id = `${oldId}-${index}`

      markdown = markdown.replaceAll(oldId, image.id)
    })

    page.markdown = markdown
  })
}

function restartTranslate(styleDefaultValue: TranslateServiceType.StyleType) {
  setTimeout(async () => {
    proPdfTranslateHelper.doReset()
    const container = document.querySelector('#pdf-markdown-content') as HTMLElement
    let renderType: RenderType = 'parallel'
    if (styleDefaultValue === 'onlyTranslation') {
      renderType = 'translation'
    } else if (styleDefaultValue === 'dualGrey') {
      renderType = 'parallel'
    } else {
      renderType = 'original'
    }
    await proPdfTranslateHelper.doTranslate('pdf', [container], renderType)
  }, 100)
}
